import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
// Supabase singleton imports (getSession, supabase.auth.onAuthStateChange) will be removed.

interface AuthContextType {
  session: Session | null; // Session might be partially constructed or derived if needed
  user: User | null;
  isLoading: boolean; // Reflects initial server load status primarily
  // signOut is removed, handled by Remix action
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  serverUser: User | null; // User state from server loader
}

export function AuthProvider({ children, serverUser }: AuthProviderProps) {
  // Initialize state directly from serverUser.
  // A full Session object might not be available or needed on the client if only user info is passed.
  // If a full Session is truly needed by consumers of AuthContext, 
  // authStateLoader would need to provide it, or we construct a minimal one.
  // For now, let's assume `user` is the primary piece of info.
  const [user, setUser] = useState<User | null>(serverUser);
  const [session, setSession] = useState<Session | null>(() => {
    // If we have a user, we can create a minimal mock session object if needed by consumers.
    // Otherwise, if consumers only need `user`, `session` could be simplified or even removed from context.
    if (serverUser) {
      return { 
        user: serverUser, 
        access_token: '', // Placeholder, actual token not passed to client via loader for security
        refresh_token: '', // Placeholder
        expires_in: 3600, // Placeholder
        token_type: 'bearer', // Placeholder
        // other session properties can be null or default if not available from loader
      } as Session;
    }
    return null;
  });
  
  // isLoading is false once AuthProvider has initialized with server data.
  const [isLoading, setIsLoading] = useState(false); 

  // If serverUser changes (e.g. after a Remix navigation/revalidation that re-runs root loader),
  // update the context state.
  useEffect(() => {
    setUser(serverUser);
    if (serverUser) {
      setSession({
        user: serverUser,
        access_token: '', refresh_token: '', expires_in: 3600, token_type: 'bearer'
      } as Session);
    } else {
      setSession(null);
    }
  }, [serverUser]);

  // The useEffect for supabase.auth.onAuthStateChange and initial getSession is removed.

  const value = {
    session,
    user,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 